/**
 * Notification service using React Native Paper Snackbar
 * Provides a centralized way to show toast notifications
 */

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export interface NotificationOptions {
  duration?: number;
  action?: {
    label: string;
    onPress: () => void;
  };
}

export interface NotificationState {
  visible: boolean;
  message: string;
  type: NotificationType;
  duration: number;
  action?: {
    label: string;
    onPress: () => void;
  };
}

type NotificationListener = (state: NotificationState) => void;

class NotificationService {
  private listeners: Set<NotificationListener> = new Set();
  private currentState: NotificationState = {
    visible: false,
    message: '',
    type: 'info',
    duration: 3000,
  };

  /**
   * Subscribe to notification state changes
   */
  subscribe(listener: NotificationListener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners
   */
  private notify() {
    this.listeners.forEach(listener => listener(this.currentState));
  }

  /**
   * Show a notification
   */
  show(
    message: string,
    type: NotificationType = 'info',
    options?: NotificationOptions
  ) {
    this.currentState = {
      visible: true,
      message,
      type,
      duration: options?.duration || 3000,
      action: options?.action,
    };
    this.notify();
  }

  /**
   * Hide the notification
   */
  hide() {
    this.currentState = {
      ...this.currentState,
      visible: false,
    };
    this.notify();
  }

  /**
   * Get current state
   */
  getState(): NotificationState {
    return {...this.currentState};
  }

  /**
   * Show success notification
   */
  success(message: string, options?: NotificationOptions) {
    this.show(message, 'success', options);
  }

  /**
   * Show error notification
   */
  error(message: string, options?: NotificationOptions) {
    this.show(message, 'error', options);
  }

  /**
   * Show info notification
   */
  info(message: string, options?: NotificationOptions) {
    this.show(message, 'info', options);
  }

  /**
   * Show warning notification
   */
  warning(message: string, options?: NotificationOptions) {
    this.show(message, 'warning', options);
  }
}

export const notificationService = new NotificationService();

